import React from 'react';
import { Bubble, Button, TableColumn } from 'tea-component';
// import { useIntl } from 'umi';
import ColumnsCenter from '@intelligence/components/TableComponents/components/CloumsCenter';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import { GenreHeader, GenreRender } from '@intelligence/components/TableComponents/components/TableGenre';
import AppMagicGenreTagList, {
  AppMagicGenreTagType,
} from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';
import {
  normalizeTableGenreSource,
  resolveAppMagicTableGenre,
  resolveTableGenreValues,
  getGenreNameI18n,
} from '@intelligence/v3/utils/genre';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import TagList, {
  ETagType,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagList';
import IgdbIcon from '@intelligence/assets/svg/gameInfo/igdb.svg';

import type { ICommonEntity } from '@intelligence/types/common';
import type {
  TableSelectionType,
  TableSelectStatusType,
} from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

import AppMagicIcon from '@intelligence/assets/imgs/dataSource/app-magic.png';

interface IDetailsColumnsType {
  onClickGame: Function;
  formatMessage: Function;
  getDisabledInfo?: (payload: any) => {
    disabled?: boolean;
    disabledMsg?: string | React.ReactElement;
  };
}

interface ICommonColumnsType {
  align?: 'left' | 'center' | 'right';
  columnsCenterAlign?: 'left' | 'center' | 'right';
  width?: number;
  placeholder?: string;
  hoverTipsKey?: string;
  // 当前语言环境
  locale?: string;
  formatMessage?: Function;
}

export const DetailsCloums = (params: IDetailsColumnsType) => {
  const { onClickGame, formatMessage = () => {}, getDisabledInfo } = params;

  // const { formatMessage } = useIntl();

  return {
    key: 'actions',
    header: formatMessage({ id: 'dbi_actions' }),
    align: 'left',
    width: 100,
    render: (cvm: any) => {
      const disabledInfo = getDisabledInfo?.(cvm);
      const disabled = disabledInfo?.disabled || false;
      const disabledMsg = disabledInfo?.disabledMsg || '';
      return (
        <Bubble dark arrowPointAtCenter placement="top-end" content={disabled ? disabledMsg : undefined}>
          <Button
            className="table-link"
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (onClickGame instanceof Function) {
                onClickGame(cvm);
              }
            }}
            type="link"
          >
            {formatMessage({ id: 'dbi_details' })}
          </Button>
        </Bubble>
      );
    },
  };
};

const CheckerColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    disabled?: boolean;
    idField?: keyof T;
    tableSelectionAll: TableSelectionType[];
    selectList: TableSelectionType[];
    onChange: (list: TableSelectionType[], status: TableSelectStatusType) => void;
  },
): TableColumn<T> => {
  const {
    disabled = false,
    idField = 'id',
    tableSelectionAll = [],
    selectList = [],
    onChange = () => {},
    width = 36,
  } = params || {};

  return {
    key: 'select',
    fixed: 'left',
    width,
    header: (
      <TableCheckboxHeader
        disabled={disabled}
        tableSelectionAll={tableSelectionAll}
        selectList={selectList}
        onChange={onChange}
      />
    ),
    render: (cvm) => (
      <TableCheckboxRender
        cvm={{
          ...(cvm || {}),
          color: '',
          gameId: cvm?.[idField],
        }}
        selectList={selectList}
        onChange={onChange}
      />
    ),
  };
};

const ReleaseTimeColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    gameIdField?: string;
    isGameDetailsMode?: boolean;
    gameDetailsLoading?: boolean;
    gameDetailsMapping?: IRecord<IRecord>;
    isSpecialReleaseTimeField?: keyof T;
    releaseTimeField?: keyof T;
    releaseTimeENField?: keyof T;
    releaseTimeCNField?: keyof T;
  },
): TableColumn<T> => {
  const {
    gameIdField = 'game_id',
    isGameDetailsMode = false,
    gameDetailsLoading = true,
    gameDetailsMapping,
    isSpecialReleaseTimeField = 'is_special_release_time',
    releaseTimeField = 'release_time',
    releaseTimeENField = 'release_time_en',
    releaseTimeCNField = 'release_time_cn',
    align = 'center',
    columnsCenterAlign = 'left',
    width = 140,
    placeholder = '-',
    locale,
    formatMessage,
    hoverTipsKey = '',
  } = params || {};

  return {
    key: 'release_time',
    align,
    width,
    header: (
      <ColumnsCenter width={width} align={columnsCenterAlign} hoverTipsKey={hoverTipsKey}>
        <div className="header-other">{formatMessage?.({ id: 'dbi_release_date' })}</div>
      </ColumnsCenter>
    ),
    render: (cvm) => {
      if (isGameDetailsMode) {
        if (gameDetailsLoading) {
          return (
            <ColumnsCenter width={width} align={columnsCenterAlign}>
              {placeholder}
            </ColumnsCenter>
          );
        }

        const gameId = cvm?.[gameIdField] || '';
        const gameDetails = gameDetailsMapping?.[gameId] as any;
        const releaseTime = formatReleaseTime(locale || '', {
          isSpecialReleaseTime: gameDetails?.[isSpecialReleaseTimeField],
          releaseTime: gameDetails?.[releaseTimeField],
          releaseTimeEN: gameDetails?.[releaseTimeENField],
          releaseTimeCN: gameDetails?.[releaseTimeCNField],
        });
        return (
          <ColumnsCenter width={width} align={columnsCenterAlign}>
            {releaseTime || placeholder}
          </ColumnsCenter>
        );
      }

      const releaseTime = formatReleaseTime(locale || '', {
        isSpecialReleaseTime: cvm?.[isSpecialReleaseTimeField],
        releaseTime: cvm?.[releaseTimeField],
        releaseTimeEN: cvm?.[releaseTimeENField],
        releaseTimeCN: cvm?.[releaseTimeCNField],
      });
      return (
        <ColumnsCenter width={width} align={columnsCenterAlign}>
          {releaseTime || placeholder}
        </ColumnsCenter>
      );
    },
  };
};

const GENRE_COLUMN_SOURCES = ['annie', 'sensortower', 'iegg'] as const;

const GenreColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    gameIdField?: string;
    isGameDetailsMode?: boolean;
    gameDetailsLoading?: boolean;
    gameDetailsMapping?: IRecord<IRecord>;
    genreSource?: IGenreSource;
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
    mainGenreField?: keyof T;
    subGenreField?: keyof T;
  },
): TableColumn<T> => {
  const {
    gameIdField = 'game_id',
    isGameDetailsMode = false,
    gameDetailsLoading = true,
    gameDetailsMapping,
    genreSource = 'iegg',
    align = 'left',
    width = 220,
    placeholder = '-',
    mainGenreField,
    subGenreField,
    locale,
    genreOptionsMap,
  } = params || {};

  const normalizedGenreSource = normalizeTableGenreSource(genreSource);

  const resolveGenreValues = (record: any) => {
    return resolveTableGenreValues(record, normalizedGenreSource, placeholder, {
      mainGenreField: mainGenreField as string | undefined,
      subGenreField: subGenreField as string | undefined,
    });
  };

  const formatGenreName = (genreValue?: string) => {
    if (!genreValue || !genreOptionsMap) {
      return genreValue || placeholder;
    }

    return genreValue
      .split('|')
      .map((value) => {
        return getGenreNameI18n({
          locale,
          genreOptionsMap,
          genreSource: normalizedGenreSource,
          genreValue: value,
        });
      })
      .join('|');
  };

  return {
    key: `genre_${normalizedGenreSource}`,
    align,
    width,
    header: (
      <div className="header-other">
        <GenreHeader genreSource={normalizedGenreSource} />
      </div>
    ),
    render: (cvm) => {
      if (isGameDetailsMode) {
        if (gameDetailsLoading) {
          return (
            <GenreRender
              genreSource={normalizedGenreSource}
              mainGenre={placeholder}
              subGenre={placeholder}
            />
          );
        }

        const gameId = cvm?.[gameIdField] || '';
        const gameDetails: any = gameDetailsMapping?.[gameId];
        const { mainGenre, subGenre } = resolveGenreValues(gameDetails);
        return (
          <GenreRender
            genreSource={normalizedGenreSource}
            mainGenre={formatGenreName(mainGenre)}
            subGenre={formatGenreName(subGenre)}
          />
        );
      }

      const { mainGenre, subGenre } = resolveGenreValues(cvm);
      return (
        <GenreRender
          genreSource={normalizedGenreSource}
          mainGenre={formatGenreName(mainGenre)}
          subGenre={formatGenreName(subGenre)}
        />
      );
    },
  };
};

/**
 * App Magic Super Genre 列
 * 展示 App Magic 数据源的一级品类（super_genre）
 */
const AppMagicSuperGenreColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    gameIdField?: string;
    isGameDetailsMode?: boolean;
    gameDetailsLoading?: boolean;
    gameDetailsMapping?: IRecord<IRecord>;
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
    onTagClick?: (type: AppMagicGenreTagType, tag: string) => void;
  },
): TableColumn<T> => {
  const {
    gameIdField = 'game_id',
    isGameDetailsMode = false,
    gameDetailsLoading = true,
    gameDetailsMapping,
    align = 'left',
    width = 160,
    placeholder = '-',
    locale,
    formatMessage,
    genreOptionsMap,
    onTagClick,
  } = params || {};

  const handleTagClick = (tag: string) => {
    onTagClick?.(AppMagicGenreTagType.SuperGenre, tag);
  };

  const formatAppMagicGenreTag = (tag: string) => {
    if (!genreOptionsMap) {
      return tag;
    }

    return getGenreNameI18n({
      locale,
      genreOptionsMap,
      genreSource: 'appmagic',
      genreValue: tag,
    });
  };

  /**
   * 渲染标签内容
   * @param dataSource 数据源对象（gameDetails 或 cvm）
   */
  const renderTagContent = (dataSource: any) => {
    const appMagicGenre = resolveAppMagicTableGenre(dataSource);
    const data = appMagicGenre.super_genre || '';
    const tags = data?.trim()?.split('|')?.filter(Boolean) || [];
    let content: React.ReactNode = placeholder;

    if (tags.length > 0) {
      content = (
        <AppMagicGenreTagList tags={tags} tagFormatter={formatAppMagicGenreTag} onTagClick={handleTagClick} />
      );
    }

    return (
      <ColumnsCenter width={width} align={align}>
        {content}
      </ColumnsCenter>
    );
  };

  return {
    key: 'super_genre',
    align,
    width,
    header: (
      <ColumnsCenter width={width} align={align}>
        <div className="general_table_genre-header">
          <img className="header-icon" src={AppMagicIcon} alt="" />
          <span>{formatMessage?.({ id: 'dbi_super_genre' })}</span>
        </div>
      </ColumnsCenter>
    ),
    render: (cvm) => {
      if (isGameDetailsMode) {
        if (gameDetailsLoading) {
          return (
            <ColumnsCenter width={width} align={align}>
              {placeholder}
            </ColumnsCenter>
          );
        }
        const gameId = cvm?.[gameIdField] || '';
        const gameDetails = gameDetailsMapping?.[gameId];
        return renderTagContent(gameDetails);
      }

      return renderTagContent(cvm);
    },
  };
};

/**
 * App Magic Genre 列
 * 展示 App Magic 数据源的二级品类（major_category）、三级品类（mid_category）和四级品类（sub_category）
 */
const AppMagicMajorGenreColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    gameIdField?: string;
    isGameDetailsMode?: boolean;
    gameDetailsLoading?: boolean;
    gameDetailsMapping?: IRecord<IRecord>;
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
    onTagClick?: (
      type: AppMagicGenreTagType,
      superTag: string,
      majorTag?: string,
      midTag?: string,
      subTag?: string,
    ) => void;
  },
): TableColumn<T> => {
  const {
    gameIdField = 'game_id',
    isGameDetailsMode = false,
    gameDetailsLoading = true,
    gameDetailsMapping,
    align = 'left',
    width = 300,
    placeholder = '-',
    locale,
    formatMessage,
    genreOptionsMap,
    onTagClick,
  } = params || {};

  const formatAppMagicGenreTag = (tag: string) => {
    if (!genreOptionsMap) {
      return tag;
    }

    return getGenreNameI18n({
      locale,
      genreOptionsMap,
      genreSource: 'appmagic',
      genreValue: tag,
    });
  };

  /**
   * 构建二级品类、三级品类和四级品类的标签列表
   * @param majorCategoryStr 二级品类字符串（用 | 分隔）
   * @param midCategoryStr 三级品类字符串（用 | 分隔）
   * @param subCategoryStr 四级品类字符串（用 | 分隔）
   * @returns 标签列表，格式为 [{ tag: string, type: 'major' | 'mid' | 'sub' }]
   */
  const buildTagList = (majorCategoryStr: string, midCategoryStr: string, subCategoryStr: string) => {
    const majorTags = majorCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];
    const midTags = midCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];
    const subTags = subCategoryStr?.trim()?.split('|')?.filter(Boolean) || [];

    const tagList: { tag: string; type: 'major' | 'mid' | 'sub' }[] = [];

    // 按顺序添加二级品类标签
    majorTags.forEach((tag) => {
      tagList.push({ tag, type: 'major' });
    });

    // 按顺序添加三级品类标签
    midTags.forEach((tag) => {
      tagList.push({ tag, type: 'mid' });
    });

    // 按顺序添加四级品类标签
    subTags.forEach((tag) => {
      tagList.push({ tag, type: 'sub' });
    });

    return tagList;
  };

  /**
   * 渲染标签内容
   * @param dataSource 数据源对象（gameDetails 或 cvm）
   */
  const renderTagContent = (dataSource: any) => {
    const appMagicGenre = resolveAppMagicTableGenre(dataSource);
    const majorCategoryStr = appMagicGenre.major_category || '';
    const midCategoryStr = appMagicGenre.mid_category || '';
    const subCategoryStr = appMagicGenre.sub_category || '';
    const tagList = buildTagList(majorCategoryStr, midCategoryStr, subCategoryStr);
    const tags = tagList.map((item) => item?.tag);

    const handleTagClickWithContext = (clickedTag: string) => {
      const superGenre = appMagicGenre.super_genre?.split('|')?.filter(Boolean)?.[0] || '';
      const majorGenre = majorCategoryStr?.split('|')?.filter(Boolean)?.[0] || '';
      const midGenre = midCategoryStr?.split('|')?.filter(Boolean)?.[0] || '';
      const tagItem = tagList.find((item) => item?.tag === clickedTag);

      if (tagItem?.type === 'major') {
        // 点击二级品类：传递一级、二级品类
        onTagClick?.(AppMagicGenreTagType.MajorGenre, superGenre, clickedTag);
      } else if (tagItem?.type === 'mid') {
        // 点击三级品类：传递一级、二级、三级品类
        onTagClick?.(AppMagicGenreTagType.MidGenre, superGenre, majorGenre, clickedTag);
      } else {
        // 点击四级品类：传递一级、二级、三级、四级品类
        onTagClick?.(AppMagicGenreTagType.SubGenre, superGenre, majorGenre, midGenre, clickedTag);
      }
    };
    let content: React.ReactNode = placeholder;

    if (tags.length > 0) {
      content = (
        <AppMagicGenreTagList
          tags={tags}
          tagFormatter={formatAppMagicGenreTag}
          onTagClick={handleTagClickWithContext}
        />
      );
    }

    return (
      <ColumnsCenter width={width} align={align}>
        {content}
      </ColumnsCenter>
    );
  };

  return {
    key: 'major_category',
    align,
    width,
    header: (
      <ColumnsCenter width={width} align={align}>
        <div className="general_table_genre-header">
          <img className="header-icon" src={AppMagicIcon} alt="" />
          <span>{formatMessage?.({ id: 'dbi_top_appmagic_genre' })}</span>
        </div>
      </ColumnsCenter>
    ),
    render: (cvm) => {
      if (isGameDetailsMode) {
        if (gameDetailsLoading) {
          return (
            <ColumnsCenter width={width} align={align}>
              {placeholder}
            </ColumnsCenter>
          );
        }
        const gameId = cvm?.[gameIdField] || '';
        const gameDetails = gameDetailsMapping?.[gameId];
        return renderTagContent(gameDetails);
      }

      return renderTagContent(cvm);
    },
  };
};

const GameEngineColumn = <T extends ICommonEntity>(
  params?: ICommonColumnsType & {
    gameIdField?: string;
    isGameDetailsMode?: boolean;
    gameDetailsLoading?: boolean;
    gameDetailsMapping?: IRecord<IRecord>;
    gameEngineField?: keyof T;
    onTagClick?: (type: string, tag: string) => void;
  },
): TableColumn<T> => {
  const {
    gameIdField = 'game_id',
    isGameDetailsMode = false,
    gameDetailsLoading = true,
    gameDetailsMapping,
    gameEngineField = 'game_engine',
    align = 'left',
    width = 160,
    placeholder = '-',
    formatMessage,
    onTagClick,
  } = params || {};

  return {
    key: 'game_engine',
    align,
    width,
    header: (
      <div className="header-other" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <img src={IgdbIcon} alt="IGDB" style={{ width: '20px', height: '20px' }} />
        {formatMessage?.({ id: 'dbi_top_game_engine' })}
      </div>
    ),
    render: (cvm, idx) => {
      let engineData: string | string[] | undefined;

      if (isGameDetailsMode) {
        if (gameDetailsLoading) {
          return (
            <ColumnsCenter width={width} align={align}>
              {placeholder}
            </ColumnsCenter>
          );
        }

        const gameId = cvm?.[gameIdField] || '';
        const gameDetails: any = gameDetailsMapping?.[gameId];
        engineData = gameDetails?.[gameEngineField];
      } else {
        engineData = cvm?.[gameEngineField as keyof T] as unknown as string | string[];
      }

      // Handle both array and string formats
      let tags: string[] | undefined;
      if (Array.isArray(engineData)) {
        tags = engineData.filter(Boolean);
      } else if (typeof engineData === 'string' && engineData.trim()) {
        tags = engineData.trim().split('|');
      }

      const hasData = tags && tags.length > 0;

      return (
        <ColumnsCenter width={width} align={align}>
          {hasData ? (
            <TagList key={idx} tags={tags} type={ETagType.GAME_ENGINE} onTagClick={onTagClick} />
          ) : (
            placeholder
          )}
        </ColumnsCenter>
      );
    },
  };
};

export default {
  CheckerColumn,
  ReleaseTimeColumn,
  GenreColumn,
  GameEngineColumn,
  AppMagicSuperGenreColumn,
  AppMagicMajorGenreColumn,
  DetailsColumn: DetailsCloums,
};
